import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Only the last 4 characters of a passport number are shown in lists. */
export function maskPassport(passportNo: string | null | undefined): string {
  if (!passportNo) return '';
  const v = passportNo.trim();
  if (v.length <= 4) return '*'.repeat(v.length);
  return '•'.repeat(Math.min(v.length - 4, 5)) + v.slice(-4);
}

/** Money comes as an exact decimal string; only add thousands separators. */
export function formatMoney(amount: string, currency?: string): string {
  const [int = '0', frac] = amount.split('.');
  const neg = int.startsWith('-');
  const digits = (neg ? int.slice(1) : int).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}${digits}${frac !== undefined ? `.${frac}` : ''}${currency ? ` ${currency}` : ''}`;
}

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const dateOnly = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? dateTime.format(new Date(iso)) : '—';
}
export function formatDate(iso: string | null | undefined): string {
  return iso ? dateOnly.format(new Date(iso)) : '—';
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const secs = Math.round((new Date(iso).getTime() - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(secs) >= size) return rtf.format(Math.round(secs / size), unit);
  }
  return 'just now';
}

/** "2030-11-12" (from <input type="date">) → "12/11/2030", the API's dd/MM/yyyy. */
export function isoToDdMmYyyy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : '';
}

/** "12/11/2030" (the API's dd/MM/yyyy) → "2030-11-12" for <input type="date">. Empty for anything else. */
export function ddMmYyyyToIso(value: string | null | undefined): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value ?? '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

export function initials(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}
